// Gates Overview: compare up to 4 games side by side, in the same layout as the concessions and
// retail Overviews (game chips + time window, headline numbers, findings, game summaries, a gate
// comparison and one chart through the day).
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { LinearProgress } from '@mui/material';
import AuthContext from '../../contexts/auth-context.js';
import { getTeam } from '../../util.js';
import Icon from '../../components/atoms/Icon.js';
import { Panel, PanelHeader, InfoTip } from '../Concession/components/ConcessionUI';
import { ChartSegmented } from '../Concession/charts/chartTheme';
import { KpiCard } from '../Concession/live/LiveComponents';
import Presence from '../Concession/components/Presence';
import OverviewInsights from '../Concession/components/OverviewInsights';
import { TimeWindow } from '../Concession/ConcessionOverview';
import OverviewCompareChart from '../Retail/overview/OverviewCompareChart';
import { bucketSeries } from '../Retail/overview/overviewMetrics';
import GateIcon from './components/GateIcon';
import { fetchGatesDay } from './gatesApi';
import { buildGateGameMetrics, buildGatesOverviewInsights, midOffset } from './gatesOverviewMetrics';
import { queueColor, minuteToClock } from './gatesMetrics';
import '../Concession/concession.css';
import '../Retail/retail.css';
import './gates.css';

const MAX_GAMES = 4;

// Same game colors as the concessions and retail Overviews
const overviewColors = [
  '#3e58c0', '#e88559', '#9792ec', '#aa4851', '#866263', '#76ba92', '#0bcaca', '#f1dc78', '#3c6e5f',
];

const fmt = (n) => (typeof n === 'number' && !Number.isNaN(n) ? Math.round(n).toLocaleString() : '--');

const SeasonSelector = ({ games, currentSeason, setCurrentSeason }) => {
  const seasons = useMemo(() => {
    const years = new Set();
    games.forEach((game) => game.dates.forEach((d) => years.add(moment(d.date).format('YYYY'))));
    return Array.from(years).sort((a, b) => b - a);
  }, [games]);
  return (
    <div className="cx-season">
      <span className="cx-chart-segment-label">Season</span>
      <div className="cx-chart-segmented">
        {seasons.map((season) => (
          <button
            key={season}
            type="button"
            className={`cx-chart-segment ${currentSeason === season ? 'active' : ''}`}
            onClick={() => setCurrentSeason(season)}
          >
            {season}
          </button>
        ))}
      </div>
    </div>
  );
};

const GATE_METRICS = [
  { value: 'entrances', label: 'Entrances' },
  { value: 'exits', label: 'Exits' },
  { value: 'queue', label: 'Peak queue' },
];

export default function GatesOverview({ currentLocation, games }) {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);

  const [currentSeason, setCurrentSeason] = useState(() => {
    const years = new Set();
    games.forEach((game) => game.dates.forEach((d) => years.add(moment(d.date).format('YYYY'))));
    return Array.from(years).sort((a, b) => b - a)[0] || moment().format('YYYY');
  });

  // Selected games keep their color while selected. Data is stored by date, so a slow
  // response can never land on the wrong game.
  const [selected, setSelected] = useState([]); // [{ date, color }]
  const [dataByDate, setDataByDate] = useState({});
  const [timeRange, setTimeRange] = useState({ startOffset: -180, endOffset: 240 });
  const [pending, setPending] = useState([]);
  const loading = pending.length > 0;

  // "Add game" dropdown
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setPickerOpen(false);
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);
  useEffect(() => {
    if (selected.length >= MAX_GAMES) setPickerOpen(false);
  }, [selected.length]);

  const findGameByDate = useCallback((date) => {
    for (const series of games) {
      const index = series.dates.findIndex((d) => d.date === date);
      if (index !== -1) return { series, gameDate: series.dates[index], index };
    }
    return null;
  }, [games]);

  const gameLabel = useCallback((date) => {
    const found = findGameByDate(date);
    const opponent = found ? getTeam(currentLocation.league, found.series.teamID)?.name : null;
    return {
      short: moment(date).format('MMM D'),
      long: moment(date).format('ddd, MMM D'),
      sub: found ? `${opponent ? `vs ${opponent} · ` : ''}Game ${found.index + 1}` : '',
      opponent,
    };
  }, [findGameByDate, currentLocation.league]);

  const fetchGame = (date) => {
    setPending((p) => [...p, date]);
    fetchGatesDay(currentLocation, authCtx, date)
      .then((gates) => setDataByDate((prev) => ({ ...prev, [date]: gates })))
      .catch((error) => {
        if (error?.status === 401) {
          authCtx.logout();
          navigate('/auth');
        } else {
          alert('Unable to fetch data. Please refresh the page and try again.');
        }
      })
      .finally(() => setPending((p) => p.filter((d) => d !== date)));
  };

  const toggleGame = (date) => {
    if (selected.some((s) => s.date === date)) {
      setSelected((prev) => prev.filter((s) => s.date !== date));
      return;
    }
    if (selected.length >= MAX_GAMES) {
      alert('Maximum amount of games is 4.');
      return;
    }
    const used = selected.map((s) => s.color);
    const color = overviewColors.find((c) => !used.includes(c)) || overviewColors[0];
    setSelected((prev) => [...prev, { date, color }]);
    if (!dataByDate[date]) fetchGame(date);
  };

  const filteredGames = games
    .filter((x) => new Date() > moment(x.dates[0].date))
    .filter((x) => moment(x.dates[0].date).format('YYYY') === currentSeason);

  // Each loaded game's numbers, recalculated when the time window changes
  const metricsByDate = useMemo(() => {
    const out = {};
    Object.entries(dataByDate).forEach(([date, gates]) => {
      out[date] = buildGateGameMetrics({ gates, date, gameDate: findGameByDate(date)?.gameDate, window: timeRange });
    });
    return out;
  }, [dataByDate, timeRange, findGameByDate]);

  const cards = selected.map((s) => ({
    ...s,
    label: gameLabel(s.date),
    pending: pending.includes(s.date),
    m: metricsByDate[s.date] || null,
  }));
  const ready = cards.filter((c) => c.m && c.m.hasData);
  const hasSelection = selected.length > 0;
  const gamesWithNoData = cards.filter((c) => c.m && !c.m.hasData);

  // Headline numbers, averaged per game
  const avgOf = (values) => {
    const v = values.filter((x) => typeof x === 'number');
    return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  };
  const kpi = useMemo(() => {
    const peakQueue = ready.reduce((top, c) => (
      c.m.peakGate && (!top || c.m.peakGate.queuePeak.value > top.value)
        ? { ...c.m.peakGate.queuePeak, gate: c.m.peakGate.label, label: c.label }
        : top
    ), null);
    const rushes = ready.filter((c) => c.m.rush);
    const rushOffsets = rushes.filter((c) => c.m.startMinute != null).map((c) => midOffset(c.m.rush, c.m.startMinute));
    return {
      entrances: avgOf(ready.map((c) => c.m.entrances)),
      exits: avgOf(ready.map((c) => c.m.exits)),
      totalEntrances: ready.reduce((sum, c) => sum + c.m.entrances, 0),
      totalExits: ready.reduce((sum, c) => sum + c.m.exits, 0),
      avgQueue: avgOf(ready.map((c) => c.m.avgQueue)),
      peakQueue,
      rush: avgOf(rushes.map((c) => c.m.rush.total)),
      rushOffset: avgOf(rushOffsets),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, metricsByDate]);

  const insights = useMemo(() => buildGatesOverviewInsights(ready), [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bold the highest game per row (only a clear winner, only when comparing)
  const bestOf = (pick) => {
    if (ready.length < 2) return null;
    const values = ready.map((c) => ({ date: c.date, v: pick(c.m) })).filter((x) => typeof x.v === 'number');
    if (values.length < 2) return null;
    const max = Math.max(...values.map((x) => x.v));
    const top = values.filter((x) => x.v === max);
    return top.length === 1 ? top[0].date : null;
  };
  const best = {
    entrances: bestOf((m) => m.entrances),
    exits: bestOf((m) => m.exits),
    queue: bestOf((m) => m.peakGate?.queuePeak.value),
    rush: bestOf((m) => m.rush?.total),
  };

  // Gates across the games
  const [gateMetric, setGateMetric] = useState('entrances');
  const gateRows = useMemo(() => {
    if (!ready.length) return [];
    const ids = Array.from(new Set(ready.flatMap((c) => c.m.perGate.map((g) => g.id))));
    const rows = ids.map((id) => {
      const info = ready.flatMap((c) => c.m.perGate).find((g) => g.id === id);
      const cells = ready.map((c) => {
        const g = c.m.perGate.find((x) => x.id === id);
        if (!g) return { date: c.date, v: null };
        if (gateMetric === 'queue') return { date: c.date, v: g.queuePeak ? g.queuePeak.value : null, minute: g.queuePeak?.minute };
        const total = gateMetric === 'entrances' ? c.m.entrances : c.m.exits;
        return { date: c.date, v: g[gateMetric], share: total ? Math.round((g[gateMetric] / total) * 100) : 0 };
      });
      const vals = cells.map((x) => x.v).filter((v) => v != null);
      return { id, label: info?.label, imageURL: info?.imageURL, cells, mean: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null };
    });
    return rows.sort((a, b) => (b.mean ?? -1) - (a.mean ?? -1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, metricsByDate, gateMetric]);
  const gateMax = Math.max(1, ...gateRows.flatMap((r) => r.cells.map((x) => x.v || 0)));

  // Chart through the day
  const metricOptions = [
    { value: 'entries', label: 'Entries' },
    { value: 'exits', label: 'Exits' },
    { value: 'queue', label: 'Queues' },
  ];
  const [metric, setMetric] = useState('entries');
  // Entries and exits as a running total (default, like the game day chart) or per interval
  const [countMode, setCountMode] = useState('total');
  const [align, setAlign] = useState('start');
  const [resolution, setResolution] = useState(15);
  const chart = useMemo(() => {
    const series = ready.map((c) => {
      const pts = metric === 'entries' ? c.m.entriesPoints : metric === 'exits' ? c.m.exitsPoints : c.m.queuePoints;
      // Running totals are drawn minute by minute; the interval only applies per interval and to queues
      const runningTotal = metric !== 'queue' && countMode === 'total';
      let points = bucketSeries(pts, { align, startMinute: c.m.startMinute, size: runningTotal ? 1 : resolution, agg: metric === 'queue' ? 'avg' : 'sum' });
      if (runningTotal) {
        // Running total from the start of the time window, as of the end of each interval
        let sum = 0;
        points = points.map((p) => {
          sum += p.y;
          return { x: p.x, y: sum };
        });
      }
      return { key: c.date, label: `${c.label.short}${c.label.opponent ? ` · ${c.label.opponent}` : ''}`, color: c.color, points };
    });
    const every = resolution === 60 ? 'hour' : resolution === 1 ? 'minute' : `${resolution} min`;
    const avgLabel = resolution === 60 ? 'hourly averages' : resolution === 1 ? 'minute by minute' : `${resolution}-minute averages`;
    const isTotal = countMode === 'total';
    const meta = {
      entries: isTotal
        ? { title: 'Entries', unit: 'entries', yTitle: 'Entries so far', subtitle: 'Running total of people entering through all gates, from the start of the time window.' }
        : { title: 'Entries', unit: 'entries', yTitle: `Entries per ${every}`, subtitle: `People entering through all gates, per ${every}. Shows when each rush happened.` },
      exits: isTotal
        ? { title: 'Exits', unit: 'exits', yTitle: 'Exits so far', subtitle: 'Running total of people leaving through all gates, from the start of the time window.' }
        : { title: 'Exits', unit: 'exits', yTitle: `Exits per ${every}`, subtitle: `People leaving through all gates, per ${every}. Shows when people left.` },
      queue: { title: 'Queues', unit: 'min', yTitle: 'Queue (min)', subtitle: `Average queue across the gates, ${avgLabel}.` },
    }[metric];
    const startMarkers = ready.filter((c) => c.m.startMinute != null).map((c) => ({ minute: c.m.startMinute, color: c.color }));
    return { series, startMarkers, ...meta };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, metricsByDate, metric, align, resolution, countMode]);

  return (
    <div className="rx-ov">
      {loading && (
        <div style={{ position: 'fixed', top: 0, left: 75, right: 0, zIndex: 9999 }}>
          <LinearProgress
            sx={{
              backgroundColor: 'rgba(2, 166, 147, 0.25)',
              height: 4,
              '& .MuiLinearProgress-bar': { backgroundColor: 'rgb(2, 166, 147)' },
            }}
          />
        </div>
      )}

      {/* Compare bar: chips for the selected games + Add game dropdown, and the time window */}
      <div className="mb-4 cx-ov-bar-wrap">
        <Panel className="cx-ov-bar">
          <div className="cx-ov-bar-row">
            <div className="cx-ov-bar-games">
              <div className="cx-ov-bar-label">
                Games
                <span className="rx-ov-count">{selected.length} of {MAX_GAMES}</span>
              </div>
              <div className="cx-ov-chips">
                {cards.map((c) => (
                  <span key={c.date} className={`cx-ov-chip cx-fade-in${c.pending ? ' pending' : ''}`}>
                    <span className="cx-ov-swatch" style={{ background: c.color }} />
                    <span className="cx-ov-chip-text rx-ov-chip-text">
                      <span>
                        {c.label.short}
                        {c.label.opponent && <span className="cx-ov-chip-sub"> · {c.label.opponent}</span>}
                      </span>
                      {c.m && c.m.windowStart != null && (
                        <span className="rx-ov-chip-window">{minuteToClock(c.m.windowStart)} – {minuteToClock(c.m.windowEnd)}</span>
                      )}
                    </span>
                    {c.pending && <span className="cx-report-spinner cx-ov-chip-spinner" aria-hidden="true" />}
                    <button type="button" className="cx-ov-chip-remove" aria-label={`Remove ${c.label.short}`} onClick={() => toggleGame(c.date)}>
                      <Icon name="x" />
                    </button>
                  </span>
                ))}
                <div className="cx-ov-add" ref={pickerRef}>
                  <button
                    type="button"
                    className="cx-ov-add-btn"
                    onClick={() => setPickerOpen((o) => !o)}
                    disabled={selected.length >= MAX_GAMES && !pickerOpen}
                    aria-expanded={pickerOpen}
                  >
                    <Icon name="plus-lg" />
                    {selected.length >= MAX_GAMES ? 'Max 4 games' : hasSelection ? 'Add game' : 'Add games to compare'}
                  </button>
                  {pickerOpen && (
                    <div className="cx-ov-picker" role="dialog" aria-label="Pick games">
                      <div className="cx-ov-picker-head">
                        <span>Pick up to 4 games</span>
                        <button type="button" className="cx-ov-chip-remove" aria-label="Close" onClick={() => setPickerOpen(false)}>
                          <Icon name="x-lg" />
                        </button>
                      </div>
                      <SeasonSelector games={games} currentSeason={currentSeason} setCurrentSeason={setCurrentSeason} />
                      <div className="cx-game-list">
                        {filteredGames.map((game, gameIndex) => {
                          const startDate = game.dates?.[0]?.date;
                          const endDate = game.dates?.[game.dates.length - 1]?.date;
                          return (
                            <div key={`game-${game.teamID}-${gameIndex}`} className="cx-game-row">
                              <div className="cx-game-info">
                                <img alt="Opponent" width={22} height={22} src={getTeam(currentLocation.league, game.teamID)?.imgURL} />
                                <span className="cx-game-name">{getTeam(currentLocation.league, game?.teamID)?.name}</span>
                                <span className="cx-game-dates">
                                  {moment(startDate).format('MMM D')} - {moment(endDate).format('MMM D')}
                                </span>
                              </div>
                              <div className="cx-game-buttons">
                                {game.dates.map((gameDate, dateIndex) => {
                                  if (!(new Date() > moment(gameDate.date))) return null;
                                  const sel = selected.find((s) => s.date === gameDate.date);
                                  return (
                                    <button
                                      key={gameDate.date}
                                      type="button"
                                      onClick={() => toggleGame(gameDate.date)}
                                      className={`btn cx-game-btn${sel ? ' selected' : ''}`}
                                      style={sel ? { backgroundColor: sel.color, borderColor: sel.color, color: '#ffffff' } : undefined}
                                    >
                                      {dateIndex + 1}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                {hasSelection && (
                  <button type="button" className="cx-ov-clear" onClick={() => setSelected([])}>
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="cx-ov-bar-window">
              <TimeWindow
                compact
                startOffset={timeRange.startOffset}
                endOffset={timeRange.endOffset}
                onStartChange={(v) => setTimeRange((t) => ({ ...t, startOffset: v }))}
                onEndChange={(v) => setTimeRange((t) => ({ ...t, endOffset: v }))}
                onPreset={(start, end) => setTimeRange({ startOffset: start, endOffset: end })}
                info={(
                  <InfoTip
                    title="Time window"
                    text="Every number, finding and chart below uses this window, measured from each game's start."
                  />
                )}
              />
            </div>
          </div>
        </Panel>
      </div>

      <Presence
        show={hasSelection}
        className="cx-ov-presence"
        fallback={(
          <div className="cx-empty-state cx-ov-empty-main">
            <Icon name="bar-chart-line" />
            <div className="cx-empty-title">No games selected</div>
            <div>Use <strong>Add games to compare</strong> above to pick up to 4 games and compare entrances, exits and gate queues side by side.</div>
          </div>
        )}
      >
        <div className="rx-ov-main">
          {gamesWithNoData.length > 0 && (
            <div className="cx-notice">
              <Icon name="info-circle" />
              No gate data for {gamesWithNoData.map((c) => c.label.short).join(', ')}.
            </div>
          )}

          {/* Headline numbers */}
          {ready.length > 0 && (
            <div className="cx-kpi-grid cx-kpi-grid-auto">
              <KpiCard label="Avg entrances" info="People who entered through every gate during the time window, averaged per game." value={fmt(kpi.entrances)}>
                <div className="cx-kpi-sub">{fmt(kpi.totalEntrances)} across {ready.length} game{ready.length === 1 ? '' : 's'}</div>
                <div className="cx-kpi-foot">Per game, averaged</div>
              </KpiCard>
              <KpiCard label="Avg exits" info="People who left through every gate during the time window, averaged per game." value={fmt(kpi.exits)}>
                <div className="cx-kpi-sub">{fmt(kpi.totalExits)} across {ready.length} game{ready.length === 1 ? '' : 's'}</div>
                <div className="cx-kpi-foot">Per game, averaged</div>
              </KpiCard>
              <KpiCard
                label="Peak queue"
                info="The longest queue reading at any gate during the time window, across the selected games."
                value={kpi.peakQueue ? <>{kpi.peakQueue.value}<span className="cx-unit">min</span></> : '—'}
                accent={kpi.peakQueue ? queueColor(kpi.peakQueue.value) : undefined}
              >
                {kpi.peakQueue && (
                  <div className="cx-kpi-sub">{kpi.peakQueue.gate} ({kpi.peakQueue.label.long}, {minuteToClock(kpi.peakQueue.minute)})</div>
                )}
                <div className="cx-kpi-foot">{kpi.avgQueue != null ? `Avg queue ${kpi.avgQueue.toFixed(1)} min across gates` : 'No queue readings'}</div>
              </KpiCard>
              <KpiCard
                label="Entry rush"
                info="The busiest 15 minutes of entries across all gates, averaged per game."
                value={kpi.rush != null ? <>{fmt(kpi.rush)}<span className="cx-unit">/ 15 min</span></> : '—'}
              >
                {kpi.rushOffset != null && (
                  <div className="cx-kpi-sub">
                    Typically {Math.abs(Math.round(kpi.rushOffset / 5) * 5) < 5
                      ? 'right at game start'
                      : `about ${Math.abs(Math.round(kpi.rushOffset / 5) * 5)} min ${kpi.rushOffset < 0 ? 'before' : 'after'} game start`}
                  </div>
                )}
                <div className="cx-kpi-foot">Per game, averaged</div>
              </KpiCard>
            </div>
          )}

          {insights.length > 0 && (
            <div className="mb-4">
              <Panel>
                <PanelHeader title="What stands out" subtitle={`Key findings across the selected game${ready.length === 1 ? '' : 's'}, most important first.`} />
                <OverviewInsights insights={insights} />
              </Panel>
            </div>
          )}

          {/* One card per game */}
          <Panel className="rx-ov-panel">
            <PanelHeader
              title="Game summaries"
              subtitle={ready.length > 1 ? 'Each selected game. The highest value in each row is bold.' : 'Each selected game.'}
            />
            <div className="rx-ov-games">
              {cards.map((c) => (
                <div key={c.date} className="rx-ov-game cx-fade-in" style={{ '--game-color': c.color }}>
                  <div className="rx-ov-game-head">
                    <span className="cx-ov-swatch" style={{ background: c.color }} />
                    <div>
                      <div className="rx-ov-game-date">{c.label.long}</div>
                      {c.label.sub && <div className="rx-ov-game-sub">{c.label.sub}</div>}
                    </div>
                  </div>
                  {!c.m ? (
                    <div className="rx-ov-game-loading">
                      <div className="cx-skel cx-skel-value" />
                      <div className="cx-skel cx-skel-line" />
                      <div className="cx-skel cx-skel-line short" />
                    </div>
                  ) : !c.m.hasData ? (
                    <div className="rx-ov-na">No gate data for this game.</div>
                  ) : (
                    <>
                      <div className={`rx-ov-game-big${best.entrances === c.date ? ' best' : ''}`}>
                        {fmt(c.m.entrances)}
                        <span>entrances</span>
                      </div>
                      <dl className="rx-ov-rows">
                        <div className={best.exits === c.date ? 'best' : ''}>
                          <dt>Exits</dt>
                          <dd>{fmt(c.m.exits)}</dd>
                        </div>
                        <div className={best.queue === c.date ? 'best' : ''}>
                          <dt>Peak queue</dt>
                          <dd>
                            {c.m.peakGate ? (
                              <>
                                <span style={{ color: queueColor(c.m.peakGate.queuePeak.value) }}>{c.m.peakGate.queuePeak.value} min</span>
                                <small> · {c.m.peakGate.label}, {minuteToClock(c.m.peakGate.queuePeak.minute)}</small>
                              </>
                            ) : '—'}
                          </dd>
                        </div>
                        <div className={best.rush === c.date ? 'best' : ''}>
                          <dt>Entry rush</dt>
                          <dd>
                            {c.m.rush ? <>{fmt(c.m.rush.total)}<small> · {minuteToClock(c.m.rush.start)}</small></> : '—'}
                          </dd>
                        </div>
                        <div>
                          <dt>Exit wave</dt>
                          <dd>{c.m.exitWave ? <>{fmt(c.m.exitWave.total)}<small> · {minuteToClock(c.m.exitWave.start)}</small></> : '—'}</dd>
                        </div>
                        <div>
                          <dt>Busiest gate</dt>
                          <dd className="rx-ov-zone">
                            {c.m.busiest ? <>{c.m.busiest.label}<small> · {Math.round((c.m.busiest.entrances / c.m.entrances) * 100)}%</small></> : '—'}
                          </dd>
                        </div>
                      </dl>
                    </>
                  )}
                </div>
              ))}
            </div>
          </Panel>

          {/* Gates across the games */}
          {gateRows.length > 0 && (
            <Panel className="rx-ov-panel">
              <PanelHeader
                title="Gates across the games"
                subtitle={gateMetric === 'queue'
                  ? 'Each gate\'s longest queue in each game. Gates with the longest queues first.'
                  : `Each gate's ${gateMetric} and share in each game. Busiest gates first.`}
                actions={<ChartSegmented options={GATE_METRICS} value={gateMetric} onChange={setGateMetric} />}
              />
              <div className="gx-ov-table-wrap">
                <table className="gx-ov-table">
                  <thead>
                    <tr>
                      <th className="gx-ov-gate-col">Gate</th>
                      {ready.map((c) => (
                        <th key={c.date}>
                          <span className="cx-ov-swatch" style={{ background: c.color }} /> {c.label.short}
                        </th>
                      ))}
                      {ready.length > 1 && <th>Average</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {gateRows.map((r) => (
                      <tr key={r.id}>
                        <td className="gx-ov-gate-col">
                          <span className="gx-ov-gate">
                            <GateIcon imageURL={r.imageURL} label={r.label || ''} />
                            {r.label}
                          </span>
                        </td>
                        {r.cells.map((x) => (
                          <td key={x.date}>
                            {x.v == null ? <span className="gx-ov-none">—</span> : gateMetric === 'queue' ? (
                              <>
                                <strong style={{ color: queueColor(x.v) }}>{x.v} min</strong>
                                <small>{minuteToClock(x.minute)}</small>
                              </>
                            ) : (
                              <>
                                <strong>{fmt(x.v)}</strong>
                                <span className="gx-ov-bar">
                                  <i style={{ width: `${(x.v / gateMax) * 100}%`, background: gateMetric === 'entrances' ? '#16a34a' : '#dc2626' }} />
                                </span>
                                <small>{x.share}%</small>
                              </>
                            )}
                          </td>
                        ))}
                        {ready.length > 1 && (
                          <td className="gx-ov-avg">
                            {r.mean == null ? '—' : gateMetric === 'queue' ? `${Math.round(r.mean)} min` : fmt(r.mean)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          {/* Through the day */}
          {ready.length > 0 && (
            <OverviewCompareChart
              metricOptions={metricOptions}
              metric={metric}
              onMetric={setMetric}
              align={align}
              onAlign={setAlign}
              series={chart.series}
              unit={chart.unit}
              yTitle={chart.yTitle}
              subtitle={chart.subtitle}
              startMarkers={chart.startMarkers}
              title={chart.title}
              resolution={resolution}
              onResolution={metric === 'queue' || countMode === 'interval' ? setResolution : undefined}
              extraControls={metric !== 'queue' ? (
                <ChartSegmented
                  options={[
                    { value: 'total', label: 'Running total' },
                    { value: 'interval', label: 'Per interval' },
                  ]}
                  value={countMode}
                  onChange={setCountMode}
                />
              ) : null}
            />
          )}
        </div>
      </Presence>
    </div>
  );
}
